import { AbsoluteFill, interpolate, useCurrentFrame } from "remotion";
import {
  Caption,
  Callout,
  COLORS,
  FONTS,
  MetricCounter,
  Panel,
  PipelineGraph,
  SceneTransition,
  SlideTimeBar,
  type PipeNode,
} from "../../coldcompute";
import { SCENE } from "./constants";
import {
  HOOK,
  STACK,
  CHAT,
  CITATIONS,
  TENANT,
  MCP,
  PAYOFF,
  OUTRO,
} from "./script";

const center: React.CSSProperties = {
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
};

const Kicker: React.FC<{ text: string; color: string }> = ({ text, color }) => (
  <div
    style={{
      fontFamily: FONTS.mono,
      fontSize: 22,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color,
      marginBottom: 22,
    }}
  >
    ▍{text}
  </div>
);

const Big: React.FC<{ children: React.ReactNode; size?: number }> = ({
  children,
  size = 92,
}) => (
  <div
    style={{
      fontFamily: FONTS.display,
      fontWeight: 700,
      fontSize: size,
      lineHeight: 0.98,
      letterSpacing: "-0.02em",
      color: COLORS.text,
      textAlign: "center",
      maxWidth: 1560,
    }}
  >
    {children}
  </div>
);

/* 01 — HOOK */
export const Scene01Hook: React.FC = () => {
  const frame = useCurrentFrame();
  const ship = interpolate(frame, [44, 74], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.hook.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="enterprise saas · solo build" color={COLORS.cyan} />
          <Big>
            ONE DEV · TWO MONTHS
            <br />
            <span style={{ color: COLORS.green, opacity: 0.4 + ship * 0.6 }}>
              SHIPPED TO PROD
            </span>
          </Big>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={HOOK} accent="cyan" />
    </AbsoluteFill>
  );
};

/* 02 — STACK */
const StackRow: React.FC<{ k: string; v: string; color: string; delay: number }> = ({
  k,
  v,
  color,
  delay,
}) => {
  const frame = useCurrentFrame();
  const o = interpolate(frame, [delay, delay + 14], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        opacity: o,
        transform: `translateX(${(1 - o) * 12}px)`,
        borderBottom: `1px solid ${COLORS.line}`,
        padding: "10px 2px",
      }}
    >
      <span style={{ fontFamily: FONTS.mono, fontSize: 20, color: COLORS.dim }}>{k}</span>
      <span style={{ fontFamily: FONTS.display, fontWeight: 600, fontSize: 26, color }}>{v}</span>
    </div>
  );
};
export const Scene02Stack: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.stack.duration} />
    <AbsoluteFill style={center}>
      <Kicker text="the whole stack" color={COLORS.green} />
      <div style={{ display: "flex", gap: 28, alignItems: "stretch" }}>
        <Panel accent="green" title="app · data · ai" width={620}>
          <StackRow k="framework" v="Next.js (standalone)" color={COLORS.text} delay={8} />
          <StackRow k="database" v="Supabase · pgvector" color={COLORS.cyan} delay={20} />
          <StackRow k="cache" v="Upstash Redis" color={COLORS.text} delay={32} />
          <StackRow k="llm + embeddings" v="OpenRouter" color={COLORS.violet} delay={44} />
        </Panel>
        <Panel accent="amber" title="where it runs" width={420}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: 168 }}>
            <div style={{ fontFamily: FONTS.mono, fontSize: 18, color: COLORS.dim }}>infra cost</div>
            <div style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 86, color: COLORS.amber, lineHeight: 1 }}>~$20</div>
            <div style={{ fontFamily: FONTS.mono, fontSize: 18, color: COLORS.dim }}>/ month · Dokploy VPS + Railway</div>
          </div>
        </Panel>
      </div>
    </AbsoluteFill>
    <Caption lines={STACK} accent="green" />
  </AbsoluteFill>
);

/* 03 — CHAT (7-stage pipeline) */
const CHAT_PIPE: PipeNode[] = [
  { label: "classify", value: "intent", accent: "cyan" },
  { label: "rewrite", value: "query", sub: "+ scope", accent: "green" },
  { label: "search", value: "HYBRID", sub: "vec + bm25", accent: "lime" },
  { label: "merge", value: "dedup", accent: "amber" },
  { label: "generate", value: "STREAM", sub: "+ grader", accent: "violet" },
];
export const Scene03Chat: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.chat.duration} />
    <AbsoluteFill style={{ ...center, padding: "0 70px" }}>
      <Kicker text="grounded chat · sources-only by default" color={COLORS.cyan} />
      <div style={{ width: 1740 }}>
        <PipelineGraph nodes={CHAT_PIPE} startFrame={8} stepFrames={20} nodeWidth={188} />
      </div>
      <div style={{ fontFamily: FONTS.body, fontSize: 26, color: COLORS.dim, marginTop: 40 }}>
        7-stage inline pipeline · answer streams with citations
      </div>
    </AbsoluteFill>
    <Caption lines={CHAT} accent="cyan" />
  </AbsoluteFill>
);

/* 04 — CITATIONS (file + score + chunk) */
const CitationCard: React.FC<{
  n: number;
  file: string;
  score: string;
  body: string;
  delay: number;
  accent: keyof typeof COLORS;
}> = ({ n, file, score, body, delay, accent }) => {
  const frame = useCurrentFrame();
  const o = interpolate(frame, [delay, delay + 16], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <div style={{ opacity: o, transform: `translateY(${(1 - o) * 16}px)` }}>
      <Panel accent={accent as never} title={`source [${n}]`} width={520}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
          <span style={{ fontFamily: FONTS.mono, fontSize: 20, color: COLORS.text }}>{file}</span>
          <span style={{ fontFamily: FONTS.mono, fontSize: 20, color: COLORS[accent] }}>score {score}</span>
        </div>
        <div style={{ fontFamily: FONTS.mono, fontSize: 17, lineHeight: 1.7, color: COLORS.dim }}>{body}</div>
      </Panel>
    </div>
  );
};
export const Scene04Citations: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.citations.duration} />
    <AbsoluteFill style={center}>
      <Kicker text="the citation panel · file + score + chunk" color={COLORS.lime} />
      <div style={{ display: "flex", gap: 28, alignItems: "flex-start" }}>
        <CitationCard
          n={1}
          file="sync-worker.ts"
          score="0.94"
          body={'"…processes documents sequentially with DOC_CONCURRENCY=1 to stay within the memory limit…"'}
          delay={16}
          accent="lime"
        />
        <CitationCard
          n={2}
          file="009_hybrid_search.sql"
          score="0.91"
          body={'"…0.7·vector + 0.3·lexical, over-fetched ×3, then re-ranked by fused score…"'}
          delay={70}
          accent="cyan"
        />
      </div>
      <div style={{ fontFamily: FONTS.body, fontSize: 26, color: COLORS.dim, marginTop: 34 }}>
        click a citation → jump to the exact passage
      </div>
    </AbsoluteFill>
    <Caption lines={CITATIONS} accent="lime" />
  </AbsoluteFill>
);

/* 05 — TENANT (RLS) */
export const Scene05Tenant: React.FC = () => {
  const frame = useCurrentFrame();
  const block = interpolate(frame, [80, 110], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.tenant.duration} />
      <AbsoluteFill style={center}>
        <Kicker text="multi-tenant · enforced in the database" color={COLORS.magenta} />
        <div style={{ display: "flex", gap: 26, alignItems: "stretch" }}>
          <Panel accent="cyan" title="tenant A" width={380}>
            <div style={{ fontFamily: FONTS.mono, fontSize: 22, lineHeight: 1.9, color: COLORS.text }}>
              <div>625 docs</div>
              <div style={{ color: COLORS.dim }}>tenant_id = A</div>
            </div>
          </Panel>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              opacity: block,
            }}
          >
            <div
              style={{
                fontFamily: FONTS.display,
                fontWeight: 700,
                fontSize: 52,
                color: COLORS.red,
                border: `2px solid ${COLORS.red}`,
                borderRadius: 999,
                width: 70,
                height: 70,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              ✕
            </div>
            <div style={{ fontFamily: FONTS.mono, fontSize: 16, color: COLORS.red, letterSpacing: "0.14em", marginTop: 8 }}>
              RLS · 0 rows
            </div>
          </div>
          <Panel accent="violet" title="tenant B" width={380}>
            <div style={{ fontFamily: FONTS.mono, fontSize: 22, lineHeight: 1.9, color: COLORS.text }}>
              <div>47 docs</div>
              <div style={{ color: COLORS.dim }}>tenant_id = B</div>
            </div>
          </Panel>
        </div>
        <div style={{ marginTop: 34 }}>
          <Callout label="009_hybrid_search.sql" accent="magenta" width={760}>
            <code style={{ fontFamily: FONTS.mono, color: COLORS.text }}>WHERE dc.tenant_id = match_tenant_id</code>
            {" "}— the query can't return another tenant's rows.
          </Callout>
        </div>
      </AbsoluteFill>
      <Caption lines={TENANT} accent="magenta" />
    </AbsoluteFill>
  );
};

/* 06 — MCP */
const McpTool: React.FC<{ name: string; desc: string; delay: number; accent: keyof typeof COLORS }> = ({
  name,
  desc,
  delay,
  accent,
}) => {
  const frame = useCurrentFrame();
  const o = interpolate(frame, [delay, delay + 14], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <div
      style={{
        opacity: o,
        transform: `translateY(${(1 - o) * 12}px)`,
        background: COLORS.panel,
        border: `1px solid ${COLORS[accent]}66`,
        borderRadius: 10,
        padding: "16px 18px",
        width: 400,
      }}
    >
      <div style={{ fontFamily: FONTS.mono, fontSize: 24, color: COLORS[accent] }}>{name}</div>
      <div style={{ fontFamily: FONTS.body, fontSize: 18, color: COLORS.dim, marginTop: 6 }}>{desc}</div>
    </div>
  );
};
export const Scene06Mcp: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.mcp.duration} />
    <AbsoluteFill style={center}>
      <Kicker text="also an MCP server · query it from your IDE" color={COLORS.violet} />
      <div style={{ display: "flex", flexWrap: "wrap", gap: 22, justifyContent: "center", width: 860 }}>
        <McpTool name="query_eakc()" desc="grounded RAG retrieval" delay={10} accent="cyan" />
        <McpTool name="list_sources()" desc="available knowledge sources" delay={26} accent="green" />
        <McpTool name="get_document()" desc="full chunk by id" delay={42} accent="lime" />
        <McpTool name="graph_query()" desc="Graphify graph traversal" delay={58} accent="violet" />
      </div>
      <div style={{ fontFamily: FONTS.mono, fontSize: 22, color: COLORS.dim, marginTop: 32 }}>
        Cursor · Claude Code · any MCP client
      </div>
    </AbsoluteFill>
    <Caption lines={MCP} accent="violet" />
  </AbsoluteFill>
);

/* 07 — PAYOFF (scoreboard) */
const ScoreRow: React.FC<{ label: string; delay: number; accent: keyof typeof COLORS }> = ({
  label,
  delay,
  accent,
}) => {
  const frame = useCurrentFrame();
  const o = interpolate(frame, [delay, delay + 14], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 14, opacity: o, transform: `translateX(${(1 - o) * 14}px)` }}>
      <span style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 30, color: COLORS[accent] }}>✓</span>
      <span style={{ fontFamily: FONTS.display, fontWeight: 600, fontSize: 38, color: COLORS.text }}>{label}</span>
    </div>
  );
};
export const Scene07Payoff: React.FC = () => {
  const frame = useCurrentFrame();
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.payoff.duration} />
      <AbsoluteFill style={center}>
        <Kicker text="the scoreboard" color={COLORS.lime} />
        <div style={{ display: "flex", gap: 80, alignItems: "center" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            <ScoreRow label="Grounded chat + citations" delay={10} accent="cyan" />
            <ScoreRow label="Multi-tenant RLS isolation" delay={26} accent="magenta" />
            <ScoreRow label="MCP server · 4 tools" delay={42} accent="violet" />
            <ScoreRow label="Production-deployed" delay={58} accent="green" />
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <MetricCounter
              value={1}
              from={0}
              startFrame={70}
              durationFrames={40}
              accent="lime"
              fontSize={200}
            />
            <div style={{ fontFamily: FONTS.mono, fontSize: 24, color: COLORS.dim, marginTop: 4, opacity: interpolate(frame, [110, 130], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" }) }}>
              developer · ~2 months · ~$20/mo
            </div>
          </div>
        </div>
      </AbsoluteFill>
      <Caption lines={PAYOFF} accent="lime" />
    </AbsoluteFill>
  );
};

/* 08 — OUTRO */
export const Scene08Outro: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.outro.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Big size={66}>
          The product is the easy part.
          <br />
          <span style={{ color: COLORS.cyan }}>The retrieval underneath is the hard part.</span>
        </Big>
        <div style={{ fontFamily: FONTS.mono, fontSize: 24, letterSpacing: "0.24em", color: COLORS.dim, marginTop: 44 }}>
          NEXT · THE RAG PIPELINE
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={OUTRO} accent="cyan" />
  </AbsoluteFill>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
